// Axis helpers of the Bodyweight trend chart (BodyweightTrendChart.js): whole-kg
// y ticks with room kept free for the labels, and x ticks in days.
// Pure functions, everything in kg.

const MAX_Y_TICKS = 8;
// Y ticks are whole kg, evenly spaced. 1 kg is the default; a wider range
// (the "All" range of a client who changed a lot) steps up so the axis
// doesn't turn into a wall of lines.
const Y_STEPS_KG = [1, 2, 5, 10, 20, 50];

export const collectKgValues = (series, goal) => {
  const values = [];
  for (const pt of series) {
    if (pt.weight !== null) values.push(pt.weight);
    if (pt.trend !== null) values.push(pt.trend);
  }
  if (goal) {
    values.push(goal.targetWeight);
    if (goal.projectedWeight !== null) values.push(goal.projectedWeight);
  }
  return values;
};

// Share of the plot height kept free on the side of the goal label, so the
// label (see ProjectionOverlay) has room inside the plot instead of sitting on
// its border or on the lines. The plot is ~254px tall, and the label needs
// ~44px above its dot or ~64px below it (GOAL_LABEL_ROOM): these are those
// pixels with a little to spare.
const GOAL_ROOM_ABOVE_FRACTION = 0.2;
const GOAL_ROOM_BELOW_FRACTION = 0.28;

// Adds ticks on the label side of each reserve ({ kg, side }) until that room
// exists. Adding ticks widens the range, so the checks repeat until none adds
// more. A bodyweight axis never goes below 0.
const reserveRoom = ({ first, last, step }, reserves) => {
  let lo = first;
  let hi = last;
  let grew = true;
  while (grew) {
    grew = false;
    for (const { kg, side } of reserves) {
      if (
        side === "above" &&
        hi * step - kg < GOAL_ROOM_ABOVE_FRACTION * (hi - lo) * step
      ) {
        hi += 1;
        grew = true;
      } else if (
        side === "below" &&
        lo > 0 &&
        kg - lo * step < GOAL_ROOM_BELOW_FRACTION * (hi - lo) * step
      ) {
        lo -= 1;
        grew = true;
      }
    }
  }
  return { first: lo, last: hi };
};

// Whole-kg ticks fitted to the data with a little headroom. `reserves` ({ kg,
// side }[]) ask for free room next to a value, on the side its label goes.
export const buildKgTicks = (valuesKg, reserves = []) => {
  const min = Math.min(...valuesKg);
  const max = Math.max(...valuesKg);
  const pad = Math.max((max - min) * 0.1, 0.5);
  const countFor = (step) =>
    Math.ceil((max + pad) / step) - Math.floor((min - pad) / step) + 1;
  const step =
    Y_STEPS_KG.find((candidate) => countFor(candidate) <= MAX_Y_TICKS) ??
    Y_STEPS_KG.at(-1);
  const fitted = {
    first: Math.max(0, Math.floor((min - pad) / step)),
    last: Math.ceil((max + pad) / step),
    step,
  };
  const { first, last } = reserveRoom(fitted, reserves);
  return Array.from({ length: last - first + 1 }, (_, i) => (first + i) * step);
};

// About 8 labels at most: weekly up to ~10 weeks, then every 2 weeks, monthly,
// bi-monthly, and beyond that whole months sized to the span.
const xTickStepDays = (spanDays) => {
  if (spanDays <= 70) return 7;
  if (spanDays <= 140) return 14;
  if (spanDays <= 270) return 30;
  if (spanDays <= 540) return 60;
  return Math.ceil(spanDays / 8 / 30) * 30;
};

// A tick every few days from the start. With a goal the last tick is the goal
// date itself (replacing the previous one when that is too close to read).
export const buildXTicks = (maxIdx, withEnd) => {
  const step = xTickStepDays(maxIdx);
  const ticks = [];
  for (let idx = 0; idx <= maxIdx; idx += step) ticks.push(idx);
  if (!withEnd) return ticks;
  const last = ticks.at(-1);
  if (last === maxIdx) return ticks;
  if (maxIdx - last < step / 2) return [...ticks.slice(0, -1), maxIdx];
  return [...ticks, maxIdx];
};
